import React from "react";
import { useTranslation } from "react-i18next";
import Select, {
  components,
  OptionProps,
  SingleValue,
  MultiValue,
} from "react-select";

import { SelectInputProps } from "./types";
import { classNames } from "utils/classNames";
import { Role } from "types";

interface RoleOption {
  value: string;
  label: string;
  description: string;
}

const roleOptions: RoleOption[] = [
  {
    value: Role.manufacturer,
    label: "roleSelect.options.manufacturer.label",
    description: "roleSelect.options.manufacturer.description",
  },
  {
    value: Role.authorizedRepresentative,
    label: "roleSelect.options.authorizedRepresentative.label",
    description: "roleSelect.options.authorizedRepresentative.description",
  },
  {
    value: Role.importer,
    label: "roleSelect.options.importer.label",
    description: "roleSelect.options.importer.description",
  },
  {
    value: Role.distributor,
    label: "roleSelect.options.distributor.label",
    description: "roleSelect.options.distributor.description",
  },
  {
    value: Role.manufacturerByModification,
    label: "roleSelect.options.manufacturerByModification.label",
    description: "roleSelect.options.manufacturerByModification.description",
  },
  {
    value: Role.quasiManufacturer,
    label: "roleSelect.options.quasiManufacturer.label",
    description: "roleSelect.options.quasiManufacturer.description",
  },
  {
    value: Role.systemIntegrator,
    label: "roleSelect.options.systemIntegrator.label",
    description: "roleSelect.options.systemIntegrator.description",
  },
];

const CustomOption: React.FC<OptionProps<RoleOption>> = (props) => {
  const { t } = useTranslation();
  const isHighlighted = props.isFocused || props.isSelected;

  return (
    <components.Option {...props}>
      <div className="py-1">
        <div
          className={classNames(
            "font-medium",
            isHighlighted ? "text-white" : "text-gray-900",
          )}
        >
          {t(props.data.label)}
        </div>
        <div
          className={classNames(
            "text-sm mt-0.5",
            isHighlighted ? "text-white/80" : "text-gray-500",
          )}
        >
          {t(props.data.description)}
        </div>
      </div>
    </components.Option>
  );
};

const styles = {
  control: (css: any) => ({
    ...css,
    borderColor: "none",
    borderWidth: "none",
    borderRadius: "none",
  }),
  option: (css: any, state: any) => ({
    ...css,
    backgroundColor: state.isFocused
      ? "#06b6d4"
      : state.isSelected
        ? "#0891b2"
        : "white",
    color: state.isFocused || state.isSelected ? "white" : "#111827",
    cursor: "pointer",
    padding: "8px 12px",
  }),
  input: (css: any) => ({
    ...css,
    input: {
      outline: "none",
    },
    "*:focus": {
      boxShadow: "none",
    },
  }),
  singleValue: (css: any) => ({
    ...css,
    color: "#111827",
  }),
  menuPortal: (css: any) => ({
    ...css,
    zIndex: 9999,
  }),
};

const RoleSelectInput: React.FunctionComponent<SelectInputProps> = (props) => {
  const { t } = useTranslation();

  const currentValue = React.useMemo(() => {
    if (props.defaultValue && typeof props.defaultValue === "string") {
      const option = roleOptions.find((o) => o.value === props.defaultValue);
      return option
        ? {
            value: option.value,
            label: t(option.label),
            description: t(option.description),
          }
        : null;
    }
    return null;
  }, [props.defaultValue, t]);

  const translatedOptions = React.useMemo(() => {
    return roleOptions.map((option) => ({
      ...option,
      label: t(option.label),
      description: t(option.description),
    }));
  }, [t]);

  const handleChange = (
    selectedOption:
      | SingleValue<{
          value: string;
          label: string;
          description: string;
        }>
      | MultiValue<{
          value: string;
          label: string;
          description: string;
        }>,
  ) => {
    if (props.onBlur) {
      console.log("Selected option:", selectedOption);

      // Since this is a single select, we only handle SingleValue
      const value =
        selectedOption && !Array.isArray(selectedOption)
          ? (selectedOption as { value: string }).value
          : "";

      props.onBlur({
        //@ts-ignore
        target: { value },
      });
    }
  };

  return (
    <div className="col-span-6">
      <div className="flex justify-between">
        <label
          htmlFor={props.id}
          className="block text-sm font-medium text-gray-700"
        >
          {t(props.label)}
        </label>
        {!props.required && (
          <span id="phone-optional" className="text-sm text-gray-400">
            optional
          </span>
        )}
      </div>
      <Select
        id={props.id}
        name={props.name}
        placeholder={t("roleSelect.placeholder")}
        required={props.required}
        value={currentValue}
        onChange={handleChange}
        className="mt-1 focus:ring-cyan-500 focus:border-cyan-500 border-gray-300 sm:text-sm"
        styles={styles}
        classNames={{
          control: ({ isFocused }) =>
            `border border-gray-300 rounded-md ${
              isFocused ? "border-cyan-500 ring-1 ring-cyan-500" : ""
            }`,
        }}
        options={translatedOptions}
        components={{
          Option: CustomOption,
        }}
        isClearable
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
      {props.info && (
        <p className="mt-2 text-sm text-gray-500">{t(props.info)}</p>
      )}
    </div>
  );
};

export default RoleSelectInput;
